/**
 * Gallery Editor Block - Frontend
 * 
 * Full gallery editor for creating and managing galleries
 * Saves to xtra_gallery CPT
 */

import { createRoot, useState, useEffect, useRef, useCallback } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

// Force style reload by adding a cache-busting link tag
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
	// Get the plugin URL from existing script tags
	const scripts = document.getElementsByTagName('script');
	let pluginUrl = '';
	for (let script of scripts) {
		if (script.src && script.src.includes('xtraxtra-blocks')) {
			const matches = script.src.match(/(.*\/xtraxtra-blocks)\//);
			if (matches) {
				pluginUrl = matches[1];
				break;
			}
		}
	}
	
	if (pluginUrl) {
		const styleUrl = `${pluginUrl}/build/blocks/xtra-gallery/style-view.css`;
		const version = '20250812-v3-' + Date.now();
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = `${styleUrl}?v=${version}`;
		link.id = 'xtra-gallery-dynamic-styles';
		
		// Remove old version if exists
		const oldLink = document.getElementById('xtra-gallery-dynamic-styles');
		if (oldLink) {
			oldLink.remove();
		}
		
		// Add new version
		document.head.appendChild(link);
	}
}

// Debounce helper
function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}

// Gallery Editor Component
function GalleryEditor({ transformationSettings, displaySettings }) {
	const [galleryId, setGalleryId] = useState(null);
	const [galleryTitle, setGalleryTitle] = useState('');
	const [galleryAuthor, setGalleryAuthor] = useState(null);
	const [images, setImages] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const [deleteTimeout, setDeleteTimeout] = useState(null);
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [columns, setColumns] = useState(3);
	const [editingCaption, setEditingCaption] = useState(null); // Track which image caption is being edited
	const [tempCaption, setTempCaption] = useState(''); // Temporary caption for modal
	const [authors, setAuthors] = useState([]); // List of authors for dropdown
	const [currentUserId, setCurrentUserId] = useState(null);
	const [canEditOthers, setCanEditOthers] = useState(false);
	const [galleryPrice, setGalleryPrice] = useState(1); // Tickets price - default to 1
	const [customAmount, setCustomAmount] = useState(''); // For custom price input
	const [selectedPriceOption, setSelectedPriceOption] = useState(1); // Track dropdown selection - default to 1
	const [galleryCommunity, setGalleryCommunity] = useState(null); // Community taxonomy
	const [communities, setCommunities] = useState([]); // List of available communities
	const [galleryStoryType, setGalleryStoryType] = useState(null); // Story type taxonomy
	const [storyTypes, setStoryTypes] = useState([]); // List of available story types
	const [galleryStatus, setGalleryStatus] = useState('draft'); // Post status
	const [galleryDate, setGalleryDate] = useState(''); // Scheduled date
	const [canPublish, setCanPublish] = useState(false); // User permission to publish
	const cloudinaryWidgetRef = useRef(null);
	const fileInputRef = useRef(null);
	
	// Pricing tiers - same as post editor
	const PRICING_TIERS = [
		{ value: 0, label: 'Basic Post - Free for Members' },
		{ value: 1, label: 'Premium Post - 1 Ticket' },
		{ value: 2, label: 'Premium Post - 2 Tickets' },
		{ value: 3, label: 'Premium Post - 3 Tickets' },
		{ value: 4, label: 'Premium Post - 4 Tickets' },
		{ value: 5, label: 'Premium Post - 5 Tickets' },
	];

	// Get gallery ID from URL params and fetch user data
	useEffect(() => {
		// Fetch current user and authors list
		fetchUserData();
		
		const urlParams = new URLSearchParams(window.location.search);
		const id = urlParams.get('gallery_id');
		if (id) {
			setGalleryId(parseInt(id));
			loadGallery(parseInt(id));
		} else {
			// New gallery
			setIsLoading(false);
		}
	}, []);
	
	// Fetch current user and authors list
	const fetchUserData = async () => {
		try {
			// Get current user
			const userResponse = await apiFetch({
				path: '/wp/v2/users/me?context=edit',
				method: 'GET'
			});
			
			setCurrentUserId(userResponse.id);
			
			// Check if user can edit others - check roles instead of capabilities
			// Administrators and editors can edit others' posts
			const userRoles = userResponse.roles || [];
			const canEdit = userRoles.includes('administrator') || userRoles.includes('editor');
			setCanEditOthers(canEdit);
			
			// Check if user can publish
			const hasPublishPermission = userRoles.includes('administrator') || userRoles.includes('editor') || userRoles.includes('author');
			setCanPublish(hasPublishPermission);
			
			// If user can edit others, fetch authors list
			if (canEdit) {
				const authorsResponse = await apiFetch({
					path: '/wp/v2/users?who=authors&per_page=100',
					method: 'GET'
				});
				
				// Format authors for dropdown
				const formattedAuthors = authorsResponse.map(author => ({
					id: author.id,
					name: author.name
				}));
				
				setAuthors(formattedAuthors);
			}
			
			// Fetch communities with hierarchy
			const communitiesResponse = await apiFetch({
				path: '/wp/v2/community?per_page=100&orderby=name&order=asc',
				method: 'GET'
			});
			
			// Build hierarchical structure
			const buildHierarchy = (communities) => {
				const communityMap = {};
				const rootCommunities = [];
				
				// First pass: create map of all communities
				communities.forEach(community => {
					communityMap[community.id] = {
						id: community.id,
						name: community.name,
						slug: community.slug,
						parent: community.parent,
						children: []
					};
				});
				
				// Second pass: build hierarchy
				communities.forEach(community => {
					if (community.parent === 0) {
						rootCommunities.push(communityMap[community.id]);
					} else if (communityMap[community.parent]) {
						communityMap[community.parent].children.push(communityMap[community.id]);
					}
				});
				
				// Sort children alphabetically
				const sortChildren = (community) => {
					community.children.sort((a, b) => a.name.localeCompare(b.name));
					community.children.forEach(sortChildren);
				};
				rootCommunities.forEach(sortChildren);
				
				// Flatten hierarchy for select options with indentation
				const flattenWithIndent = (communities, level = 0) => {
					const result = [];
					communities.forEach(community => {
						// Use non-breaking spaces and tree characters for better visualization
						const indent = '\u00A0\u00A0\u00A0\u00A0'.repeat(level); // 4 non-breaking spaces per level
						const prefix = level > 0 ? '└─ ' : '';
						result.push({
							id: community.id,
							name: community.name,
							slug: community.slug,
							level: level,
							displayName: indent + prefix + community.name
						});
						if (community.children.length > 0) {
							result.push(...flattenWithIndent(community.children, level + 1));
						}
					});
					return result;
				};
				
				return flattenWithIndent(rootCommunities);
			};
			
			const hierarchicalCommunities = buildHierarchy(communitiesResponse);
			setCommunities(hierarchicalCommunities);
			
			// Fetch story types with hierarchy if enabled
			if (displaySettings?.showStoryType) {
				const storyTypesResponse = await apiFetch({
					path: '/wp/v2/story_type?per_page=100&orderby=name&order=asc',
					method: 'GET'
				});
				
				const hierarchicalStoryTypes = buildHierarchy(storyTypesResponse);
				setStoryTypes(hierarchicalStoryTypes);
				
				// Set default story type to "general" for new galleries
				const urlParams = new URLSearchParams(window.location.search);
				if (!urlParams.get('gallery_id')) {
					const generalStoryType = hierarchicalStoryTypes.find(st => 
						st.slug === 'general' || st.name.toLowerCase() === 'general'
					);
					if (generalStoryType) {
						setGalleryStoryType(generalStoryType.id);
					}
				}
			}
			
			// Set default community to Earth if it exists and no gallery is being edited
			const earthCommunity = hierarchicalCommunities.find(c => 
				c.slug === 'earth' || c.name.toLowerCase() === 'earth'
			);
			const urlParams = new URLSearchParams(window.location.search);
			if (earthCommunity && !urlParams.get('gallery_id')) {
				setGalleryCommunity(earthCommunity.id);
			}
			
			// Set default author for new galleries
			if (!galleryId) {
				setGalleryAuthor(userResponse.id);
			}
		} catch (err) {
			console.error('Failed to fetch user data:', err);
		}
	};

	// Load existing gallery
	const loadGallery = async (id) => {
		setIsLoading(true);
		setError(null);
		
		try {
			const response = await apiFetch({
				path: `/xtraxtra/v1/gallery/${id}`,
				method: 'GET'
			});
			
			setGalleryTitle(response.title || '');
			setImages(response.images || []);
			setGalleryAuthor(response.author || currentUserId);
			
			// Handle price setting - check if it's a custom amount
			const price = response.price !== undefined ? response.price : 0;
			setGalleryPrice(price);
			const isCustomPrice = price !== 0 && !PRICING_TIERS.find(tier => tier.value === price);
			if (isCustomPrice) {
				setSelectedPriceOption('custom');
				setCustomAmount(price.toString());
			} else {
				setSelectedPriceOption(price);
			}
			
			setGalleryCommunity(response.community || null);
			setGalleryStoryType(response.story_type || null);
			setGalleryStatus(response.status || 'draft');
			setGalleryDate(response.date || '');
		} catch (err) {
			setError(__('Failed to load gallery. Please check the URL and try again.', 'xtraxtra-blocks'));
		} finally {
			setIsLoading(false);
		}
	};

	// Create new gallery
	const createGallery = async () => {
		setIsSaving(true);
		setError(null);
		
		try {
			const response = await apiFetch({
				path: '/xtraxtra/v1/gallery/create',
				method: 'POST',
				data: {
					title: galleryTitle || __('Untitled Gallery', 'xtraxtra-blocks')
				}
			});
			
			setGalleryId(response.gallery_id);
			setSuccess(__('Gallery created successfully!', 'xtraxtra-blocks'));
			
			// Update URL without reload
			const newUrl = `${window.location.pathname}?gallery_id=${response.gallery_id}`;
			window.history.replaceState({}, '', newUrl);
			
			return response.gallery_id;
		} catch (err) {
			setError(__('Failed to create gallery.', 'xtraxtra-blocks'));
			throw err;
		} finally {
			setIsSaving(false);
		}
	};

	// Save gallery (create or update)
	const saveGallery = async () => {
		let currentGalleryId = galleryId;
		
		// Create gallery if it doesn't exist
		if (!currentGalleryId) {
			try {
				currentGalleryId = await createGallery();
			} catch (err) {
				return;
			}
		}
		
		setIsSaving(true);
		setError(null);
		setSuccess(null);
		
		try {
			await apiFetch({
				path: `/xtraxtra/v1/gallery/${currentGalleryId}/update`,
				method: 'POST',
				data: {
					gallery_id: currentGalleryId,
					title: galleryTitle,
					author: galleryAuthor || currentUserId,
					price: galleryPrice,
					community: galleryCommunity,
					story_type: galleryStoryType,
					status: galleryStatus,
					date: galleryStatus === 'future' ? galleryDate : '',
					images: images.map((img, index) => ({
						...img,
						position: index
					}))
				}
			});
			
			setSuccess(__('Gallery saved successfully!', 'xtraxtra-blocks'));
			setTimeout(() => setSuccess(null), 3000);
		} catch (err) {
			setError(__('Failed to save gallery.', 'xtraxtra-blocks'));
		} finally {
			setIsSaving(false);
		}
	};

	// Removed auto-save functionality - now manual save only
	// const autoSave = useCallback(
	// 	debounce(() => {
	// 		if (galleryId && images.length > 0) {
	// 			saveGallery();
	// 		}
	// 	}, 2000),
	// 	[galleryId, images, galleryTitle]
	// );

	// Auto-save on changes - DISABLED for manual save only
	// useEffect(() => {
	// 	if (galleryId) {
	// 		autoSave();
	// 	}
	// }, [images]);

	// Cleanup widget on unmount
	useEffect(() => {
		return () => {
			if (cloudinaryWidgetRef.current) {
				try {
					cloudinaryWidgetRef.current.destroy();
					cloudinaryWidgetRef.current = null;
				} catch (e) {
					console.warn('Failed to cleanup widget:', e);
				}
			}
		};
	}, []);
	
	// Helper function to generate Cloudinary thumbnail URL with transformations
	const getCloudinaryThumbnail = (url) => {
		if (!url) return '';
		
		// Check if it's already a Cloudinary URL
		if (url.includes('cloudinary.com')) {
			// Build transformation string from settings
			const transforms = [];
			
			// Use transformation settings if provided
			if (transformationSettings) {
				if (transformationSettings.thumbnailWidth) {
					transforms.push(`w_${transformationSettings.thumbnailWidth}`);
				} else {
					transforms.push('w_300'); // Default width
				}
				
				if (transformationSettings.thumbnailHeight) {
					transforms.push(`h_${transformationSettings.thumbnailHeight}`);
				} else {
					transforms.push('h_300'); // Default height
				}
				
				if (transformationSettings.thumbnailAspectRatio) {
					transforms.push(`ar_${transformationSettings.thumbnailAspectRatio}`);
				}
				
				if (transformationSettings.thumbnailCrop) {
					transforms.push(`c_${transformationSettings.thumbnailCrop}`);
				} else {
					transforms.push('c_fill'); // Default crop
				}
				
				// Add gravity if crop mode requires it
				if (transformationSettings.thumbnailGravity && 
					(transformationSettings.thumbnailCrop === 'fill' || 
					 transformationSettings.thumbnailCrop === 'crop' || 
					 transformationSettings.thumbnailCrop === 'thumb')) {
					transforms.push(`g_${transformationSettings.thumbnailGravity}`);
				}
				
				if (transformationSettings.thumbnailQuality) {
					transforms.push(`q_${transformationSettings.thumbnailQuality}`);
				} else {
					transforms.push('q_auto'); // Default quality
				}
				
				if (transformationSettings.thumbnailFormat) {
					transforms.push(`f_${transformationSettings.thumbnailFormat}`);
				} else {
					transforms.push('f_auto'); // Default format
				}
				
				if (transformationSettings.thumbnailDpr) {
					transforms.push(`dpr_${transformationSettings.thumbnailDpr}`);
				}
				
				// Add custom transformations
				if (transformationSettings.thumbnailTransform) {
					transforms.push(transformationSettings.thumbnailTransform);
				}
			} else {
				// Fallback to default transformations
				transforms.push('c_fill', 'w_300', 'h_300', 'q_auto', 'f_auto');
			}
			
			// Insert transformation before the version or image path
			const parts = url.split('/upload/');
			if (parts.length === 2) {
				return parts[0] + '/upload/' + transforms.join(',') + '/' + parts[1];
			}
		}
		
		// Fallback to original URL if not a Cloudinary URL
		return url;
	};

	// Open Cloudinary upload widget
	const openCloudinaryUpload = () => {
		if (!window.cloudinary) {
			const script = document.createElement('script');
			script.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
			script.async = true;
			script.onload = () => {
				createUploadWidget();
			};
			document.body.appendChild(script);
		} else {
			createUploadWidget();
		}
	};

	const handleModerationFailure = async (cloudinaryData, status) => {
		try {
			// Log to moderation table
			await apiFetch({
				path: '/xtraxtra/v1/cloudinary/moderation-log',
				method: 'POST',
				data: {
					user_id: currentUserId || window.XTRA_CLOUDINARY?.user_id || 0,
					public_id: cloudinaryData.public_id,
					url: cloudinaryData.url,
					secure_url: cloudinaryData.secure_url,
					upload_context: 'gallery',
					context_id: galleryId || 0,
					field_type: 'gallery_image',
					moderation_status: status,
					moderation_kind: cloudinaryData.moderation?.[0]?.kind || '',
					moderation_response: JSON.stringify(cloudinaryData.moderation || {})
				}
			});
			
			// Show user-friendly message
			setError('Image rejected per moderation rules. If you have questions, please contact us.');
			setTimeout(() => setError(null), 5000);
			
		} catch (error) {
			console.error('Failed to log moderation failure:', error);
		}
	};

	const createUploadWidget = () => {
		const config = window.XTRA_CLOUDINARY || window.cloudinaryConfig || window.xtraGalleryConfig || {};
		
		// Check if configuration is available
		if (!config.cloud_name) {
			console.error('Cloudinary configuration not found. Please ensure XTRA_CLOUDINARY is configured.');
			setError('Cloudinary configuration not found. Please contact administrator.');
			return;
		}
		
		const userId = config.user_id || config.userId || 1;
		const timestamp = Date.now();
		const year = new Date().getFullYear();

		// Destroy existing widget safely
		if (cloudinaryWidgetRef.current) {
			try {
				cloudinaryWidgetRef.current.destroy();
				cloudinaryWidgetRef.current = null;
			} catch (e) {
				console.warn('Failed to destroy existing widget:', e);
			}
		}

		// Set folder path for gallery images: /xtraxtra/live/users/ID/galleries/YYYY
		const folderPath = `xtraxtra/live/users/${userId}/galleries/${year}`;

		// Create widget but don't open it immediately
		try {
			const widget = window.cloudinary.createUploadWidget({
				cloudName: config.cloud_name,
				uploadPreset: config.upload_preset || 'xtraxtra-gallery-upload',
				folder: folderPath, // Dynamic folder path with user ID and year
				sources: ['local'],
				multiple: true,
				maxFiles: 50,
				clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'avif', 'gif'],
				resourceType: 'image',
				tags: [`user_${userId}`, galleryId ? `gallery_cpt_${galleryId}` : 'gallery_new'],
				context: `uploaded_by=${userId}|gallery_cpt=${galleryId || 'new'}|uploaded_at=${timestamp}`,
				showPoweredBy: false,
				styles: {
					palette: {
						window: '#0a0a0a',
						windowBorder: '#333333',
						tabIcon: '#3498db',
						menuIcons: '#a0a0a0',
						textDark: '#ffffff',
						textLight: '#a0a0a0',
						link: '#3498db',
						action: '#3498db',
						inactiveTabIcon: '#666666',
						error: '#e74c3c',
						inProgress: '#3498db',
						complete: '#2ecc71',
						sourceBg: '#1a1a1a'
					}
				}
			}, (error, result) => {
			if (error) {
				console.error('Upload error:', error);
				return;
			}

			if (result.event === 'success') {
				// Check moderation status if present
				if (result.info.moderation) {
					const moderationStatus = result.info.moderation[0]?.status || 'approved';
					
					if (moderationStatus === 'rejected' || moderationStatus === 'pending') {
						// Log to moderation table
						handleModerationFailure(result.info, moderationStatus);
						return; // Don't add to gallery
					}
				}
				
				// Only add to gallery if approved or no moderation
				const newImage = {
					publicId: result.info.public_id,
					url: result.info.secure_url,
					width: result.info.width,
					height: result.info.height,
					bytes: result.info.bytes || 0,
					alt: '',
					caption: ''
				};

				setImages(prev => [...prev, newImage]);

				// Track the upload
				apiFetch({
					path: '/xtraxtra/v1/cloudinary/track-upload',
					method: 'POST',
					data: {
						cloudinary_data: {
							public_id: result.info.public_id,
							url: result.info.url,
							secure_url: result.info.secure_url,
							width: result.info.width,
							height: result.info.height,
							bytes: result.info.bytes,
							format: result.info.format,
							version: result.info.version,
							resource_type: result.info.resource_type,
							created_at: result.info.created_at,
							tags: result.info.tags,
							folder: result.info.folder
						}
					}
				}).catch(error => {
					console.error('Failed to track image:', error);
				});
			}

			if (result.event === 'close') {
				// Manual save only - user must click Save Gallery button
				// Removed auto-save after uploads complete
			}
		});
			
			cloudinaryWidgetRef.current = widget;
			
			// Open widget after a small delay to ensure DOM is ready
			setTimeout(() => {
				if (cloudinaryWidgetRef.current) {
					cloudinaryWidgetRef.current.open();
				}
			}, 100);
		} catch (error) {
			console.error('Failed to create upload widget:', error);
			setError('Failed to initialize upload widget. Please refresh the page and try again.');
		}
	};

	// Remove image
	const removeImage = (index) => {
		setImages(prev => prev.filter((_, i) => i !== index));
	};

	// Update image metadata
	const updateImage = (index, field, value) => {
		setImages(prev => {
			const newImages = [...prev];
			newImages[index] = {
				...newImages[index],
				[field]: value
			};
			return newImages;
		});
	};

	// Drag and drop handlers
	const handleDragStart = (e, index) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleDrop = (e, dropIndex) => {
		e.preventDefault();
		
		if (draggedIndex === null) return;
		
		const draggedImage = images[draggedIndex];
		const newImages = [...images];
		
		// Remove from old position
		newImages.splice(draggedIndex, 1);
		
		// Insert at new position
		newImages.splice(dropIndex, 0, draggedImage);
		
		setImages(newImages);
		setDraggedIndex(null);
	};

	// Delete gallery
	const deleteGallery = async () => {
		if (!galleryId) return;
		
		// First tap - show confirmation
		if (!deleteConfirm) {
			setDeleteConfirm(true);
			
			// Reset confirmation after 3 seconds
			const timeout = setTimeout(() => {
				setDeleteConfirm(false);
			}, 3000);
			
			setDeleteTimeout(timeout);
			return;
		}
		
		// Second tap - actually delete
		if (deleteTimeout) {
			clearTimeout(deleteTimeout);
		}
		
		setIsSaving(true);
		setError(null);
		setDeleteConfirm(false);
		
		try {
			await apiFetch({
				path: `/xtraxtra/v1/gallery/${galleryId}/delete`,
				method: 'DELETE'
			});
			
			setSuccess(__('Gallery deleted. Redirecting...', 'xtraxtra-blocks'));
			
			// Redirect to gallery list page
			setTimeout(() => {
				window.location.href = '/my-galleries';
			}, 1500);
		} catch (err) {
			setError(__('Failed to delete gallery.', 'xtraxtra-blocks'));
		} finally {
			setIsSaving(false);
		}
	};

	// Render loading state
	if (isLoading) {
		return (
			<div className="xtra-gallery-editor">
				<div className="xtra-gallery-editor__loading">
					<span className="spinner is-active"></span>
					<p>{__('Loading gallery...', 'xtraxtra-blocks')}</p>
				</div>
			</div>
		);
	}

	// Render editor
	return (
		<div className="xtra-gallery-editor">
			{/* Header */}
			<div className="xtra-gallery-editor__header">
				{/* Title field - conditional based on settings */}
				{displaySettings?.showTitle !== false && (
					<div className="xtra-gallery-editor__title-group">
						<label className="xtra-gallery-editor__title-label">
							{__('Title', 'xtraxtra-blocks')}
						</label>
						<input
							type="text"
							className="xtra-gallery-editor__title"
							placeholder={__('Enter gallery title', 'xtraxtra-blocks')}
							value={galleryTitle}
							onChange={(e) => setGalleryTitle(e.target.value)}
						/>
					</div>
				)}
				
				{/* Author selector - only show if enabled and user has permission */}
				{displaySettings?.showAuthor !== false && canEditOthers && authors.length > 0 && (
					<div className="xtra-gallery-editor__author-group">
						<label className="xtra-gallery-editor__author-label">
							{__('Author', 'xtraxtra-blocks')}
						</label>
						<select
							className="xtra-gallery-editor__author-select"
							value={galleryAuthor || currentUserId}
							onChange={(e) => setGalleryAuthor(parseInt(e.target.value))}
						>
							{authors.map(author => (
								<option key={author.id} value={author.id}>
									{author.name}
								</option>
							))}
						</select>
					</div>
				)}
				
				{/* Community Selector */}
				<div className="xtra-gallery-editor__author-group">
					<label className="xtra-gallery-editor__author-label">
						{__('Community', 'xtraxtra-blocks')}
					</label>
					<select
						className="xtra-gallery-editor__author-select"
						value={galleryCommunity || ''}
						onChange={(e) => setGalleryCommunity(e.target.value ? parseInt(e.target.value) : null)}
					>
						{communities.map(community => (
							<option 
								key={community.id} 
								value={community.id}
							>
								{community.displayName || community.name}
							</option>
						))}
					</select>
				</div>
				
				{/* Story Type (conditional) */}
				{displaySettings?.showStoryType && (
					<div className="xtra-gallery-editor__community-group">
						<label className="xtra-gallery-editor__author-label">
							{__('Gallery Type', 'xtraxtra-blocks')}
						</label>
						<select
							className="xtra-gallery-editor__author-select"
							value={galleryStoryType || ''}
							onChange={(e) => setGalleryStoryType(e.target.value ? parseInt(e.target.value) : null)}
						>
							{storyTypes.map(storyType => (
								<option 
									key={storyType.id} 
									value={storyType.id}
								>
									{storyType.displayName || storyType.name}
								</option>
							))}
						</select>
					</div>
				)}
				
				{/* Tickets Unlock Cost */}
				<div className="xtra-gallery-editor__unlock-cost-group">
					<label className="xtra-gallery-editor__unlock-label">
						{__('Member Unlock Cost', 'xtraxtra-blocks')}
					</label>
					<select
						className="xtra-gallery-editor__unlock-select"
						value={selectedPriceOption}
						onChange={(e) => {
							const value = e.target.value;
							setSelectedPriceOption(value);
							
							if (value === 'custom') {
								// When switching to custom, clear the custom amount
								setCustomAmount('');
								// Don't change gallery price yet
							} else {
								// For preset tiers, update immediately
								const price = parseInt(value);
								setGalleryPrice(price);
								setSelectedPriceOption(price);
							}
						}}
					>
						{PRICING_TIERS.map(tier => (
							<option key={tier.value} value={tier.value}>
								{tier.label}
							</option>
						))}
						{displaySettings.allowCustomAmount && (
							<option value="custom">
								Premium Post - Custom amount
							</option>
						)}
					</select>
					
					{selectedPriceOption === 'custom' && displaySettings.allowCustomAmount && (
						<div className="xtra-gallery-editor__custom-amount">
							<label className="xtra-gallery-editor__unlock-label">
								{__('Enter the member cost in Tickets', 'xtraxtra-blocks')}
							</label>
							<input
								type="number"
								className="xtra-gallery-editor__custom-input"
								value={customAmount}
								onChange={(e) => {
									const val = e.target.value;
									setCustomAmount(val);
									
									// Validate and update gallery price
									const amount = parseInt(val, 10);
									if (!isNaN(amount) && amount >= 1 && amount <= 2000) {
										setGalleryPrice(amount);
									}
								}}
								min="1"
								max="2000"
								step="1"
								placeholder="1-2000"
								disabled={isSaving}
							/>
						</div>
					)}
					
					<div className="xtra-gallery-editor__unlock-help">
						Non-members pay a higher price and you'll receive bonus MyBucks from those unlocks.
					</div>
				</div>
				
				{/* Status Field */}
				<div className="xtra-gallery-editor__community-group">
					<label className="xtra-gallery-editor__author-label">
						{__('Status', 'xtraxtra-blocks')}
					</label>
					<select
						className="xtra-gallery-editor__author-select"
						value={galleryStatus}
						onChange={(e) => {
							const newStatus = e.target.value;
							setGalleryStatus(newStatus);
							
							// If switching to scheduled and no date is set, default to 1 hour from now
							if (newStatus === 'future' && !galleryDate) {
								const futureDate = new Date();
								futureDate.setHours(futureDate.getHours() + 1);
								// Format for datetime-local input (YYYY-MM-DDTHH:mm)
								const formattedDate = futureDate.toISOString().slice(0, 16);
								setGalleryDate(formattedDate);
							}
						}}
						disabled={isSaving}
					>
						<option value="draft">{__('Draft', 'xtraxtra-blocks')}</option>
						<option value="pending">{__('Pending Review', 'xtraxtra-blocks')}</option>
						{canPublish && (
							<>
								<option value="publish">{__('Published', 'xtraxtra-blocks')}</option>
								<option value="future">{__('Scheduled', 'xtraxtra-blocks')}</option>
							</>
						)}
					</select>
					
					{/* Date picker for scheduled posts */}
					{galleryStatus === 'future' && (
						<div className="xtra-gallery-editor__community-group" style={{marginTop: '1rem'}}>
							<label className="xtra-gallery-editor__author-label">
								{__('Schedule Date & Time', 'xtraxtra-blocks')}
							</label>
							<input
								type="datetime-local"
								className="xtra-gallery-editor__author-select"
								style={{padding: '0.5rem'}}
								value={galleryDate}
								onChange={(e) => setGalleryDate(e.target.value)}
								min={new Date().toISOString().slice(0, 16)}
								disabled={isSaving}
								required
							/>
						</div>
					)}
				</div>
				
				{/* Divider between metadata and gallery content */}
				<div className="xtra-gallery-editor__divider"></div>
				
				{/* Action buttons */}
				<div className="xtra-gallery-editor__actions">
					<div className="xtra-gallery-editor__button-row">
						<button
							className="xtra-button xtra-button--secondary"
							onClick={openCloudinaryUpload}
						>
							{__('Upload Images', 'xtraxtra-blocks')}
						</button>
						
						{galleryId && (
							<button
								className={`xtra-button xtra-button--danger ${deleteConfirm ? 'xtra-button--confirm' : ''}`}
								onClick={deleteGallery}
								disabled={isSaving}
							>
								{deleteConfirm 
									? __('Tap to Confirm Delete', 'xtraxtra-blocks') 
									: __('Delete Gallery', 'xtraxtra-blocks')}
							</button>
						)}
					</div>
					
					<button
						className="xtra-button xtra-button--primary xtra-button--full"
						onClick={saveGallery}
						disabled={isSaving}
					>
						{isSaving ? __('Saving...', 'xtraxtra-blocks') : __('Save Gallery', 'xtraxtra-blocks')}
					</button>
					
					{galleryId && (
						<div className="xtra-gallery-editor__view-link">
							<a 
								href={`/?p=${galleryId}`}
								target="_blank"
								rel="noopener noreferrer"
								className="xtra-gallery-editor__view-link-text"
							>
								{__('View Gallery', 'xtraxtra-blocks')} →
							</a>
						</div>
					)}
				</div>
			</div>

			{/* Messages */}
			{error && (
				<div className="xtra-gallery-editor__notice xtra-gallery-editor__notice--error">
					{error}
				</div>
			)}
			
			{success && (
				<div className="xtra-gallery-editor__notice xtra-gallery-editor__notice--success">
					{success}
				</div>
			)}

			{/* Column Control */}
			{images.length > 0 && (
				<div className="xtra-gallery-editor__controls">
					<label className="xtra-gallery-editor__column-control">
						<span>{__('Columns:', 'xtraxtra-blocks')}</span>
						<input
							type="range"
							min="1"
							max="6"
							value={columns}
							onChange={(e) => setColumns(parseInt(e.target.value))}
							className="xtra-gallery-editor__column-slider"
						/>
						<span className="xtra-gallery-editor__column-value">{columns}</span>
					</label>
				</div>
			)}

			{/* Image Grid */}
			<div className="xtra-gallery-editor__content">
				{images.length === 0 ? (
					<div className="xtra-gallery-editor__empty">
						<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
							<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
							<circle cx="8.5" cy="8.5" r="1.5"></circle>
							<polyline points="21 15 16 10 5 21"></polyline>
						</svg>
						<h3>{__('No images in gallery', 'xtraxtra-blocks')}</h3>
						<p>{__('Upload images to get started', 'xtraxtra-blocks')}</p>
						<button
							className="xtra-button xtra-button--primary"
							onClick={openCloudinaryUpload}
						>
							{__('Upload Images', 'xtraxtra-blocks')}
						</button>
					</div>
				) : (
					<div className="xtra-gallery-editor__grid" style={{ '--xtra-gallery-columns': columns }}>
						{images.map((image, index) => (
							<div
								key={image.publicId}
								className="xtra-gallery-editor__item"
								draggable
								onDragStart={(e) => handleDragStart(e, index)}
								onDragOver={handleDragOver}
								onDrop={(e) => handleDrop(e, index)}
							>
								<div className="xtra-gallery-editor__item-image">
									<img
										src={getCloudinaryThumbnail(image.url)}
										alt={image.alt || ''}
									/>
									{image.caption && (
										<div className="xtra-gallery-editor__item-caption-indicator" title={image.caption}>
											<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
												<path d="M3 21v-2h18v2H3zm0-4v-2h18v2H3zm0-4v-2h18v2H3zm0-4V7h18v2H3zm0-4V3h18v2H3z"/>
											</svg>
										</div>
									)}
									<button
										className="xtra-gallery-editor__item-remove"
										onClick={() => removeImage(index)}
										aria-label={__('Remove image', 'xtraxtra-blocks')}
									>
										×
									</button>
									<button
										className="xtra-gallery-editor__item-edit"
										onClick={() => {
											setEditingCaption(index);
											setTempCaption(image.caption || '');
										}}
										aria-label={__('Edit caption', 'xtraxtra-blocks')}
									>
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
										</svg>
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Footer Stats */}
			{images.length > 0 && (
				<div className="xtra-gallery-editor__footer">
					<span>{sprintf(__('%d images', 'xtraxtra-blocks'), images.length)}</span>
					{galleryId && (
						<span>{__('Manual save only', 'xtraxtra-blocks')}</span>
					)}
				</div>
			)}
			
			{/* Caption Modal */}
			{editingCaption !== null && (
				<div className="xtra-gallery-editor__caption-modal" onClick={(e) => {
					if (e.target === e.currentTarget) {
						setEditingCaption(null);
					}
				}}>
					<div className="xtra-gallery-editor__caption-modal-content">
						<div className="xtra-gallery-editor__caption-modal-header">
							<h3>{__('Edit Caption', 'xtraxtra-blocks')}</h3>
							<button onClick={() => setEditingCaption(null)}>×</button>
						</div>
						<div className="xtra-gallery-editor__caption-modal-body">
							<textarea
								value={tempCaption}
								onChange={(e) => setTempCaption(e.target.value)}
								placeholder={__('Enter caption for this image...', 'xtraxtra-blocks')}
								autoFocus
							/>
						</div>
						<div className="xtra-gallery-editor__caption-modal-footer">
							<button 
								className="xtra-button xtra-button--secondary"
								onClick={() => setEditingCaption(null)}
							>
								{__('Cancel', 'xtraxtra-blocks')}
							</button>
							<button 
								className="xtra-button xtra-button--primary"
								onClick={() => {
									updateImage(editingCaption, 'caption', tempCaption);
									setEditingCaption(null);
								}}
							>
								{__('Save Caption', 'xtraxtra-blocks')}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
	// Look for either the block class or the container class
	const container = document.querySelector('.wp-block-xtra-xtra-gallery, .xtra-gallery-editor-container');
	if (container) {
		// Check permission flag first
		const permission = container.dataset.permission;
		
		// If permission is denied, don't initialize React
		// The PHP has already rendered the error message
		if (permission === 'denied') {
			console.log('Gallery editor: Permission denied');
			return;
		}
		
		// Only initialize if permission is granted
		if (permission !== 'granted') {
			console.log('Gallery editor: No permission flag found');
			return;
		}
		
		// Set up API
		if (window.XXRest) {
			apiFetch.use(apiFetch.createNonceMiddleware(window.XXRest.nonce));
			apiFetch.use(apiFetch.createRootURLMiddleware(window.XXRest.root));
		}
		
		// Read settings from data attributes
		const transformationSettings = {
			thumbnailWidth: container.dataset.thumbnailWidth || '',
			thumbnailHeight: container.dataset.thumbnailHeight || '',
			thumbnailAspectRatio: container.dataset.thumbnailAspectRatio || '',
			thumbnailCrop: container.dataset.thumbnailCrop || 'fill',
			thumbnailGravity: container.dataset.thumbnailGravity || 'auto',
			thumbnailQuality: container.dataset.thumbnailQuality || 'auto',
			thumbnailFormat: container.dataset.thumbnailFormat || 'auto',
			thumbnailDpr: container.dataset.thumbnailDpr || '',
			thumbnailTransform: container.dataset.thumbnailTransform || ''
		};
		
		const displaySettings = {
			showTitle: container.dataset.showTitle !== 'false',
			showAuthor: container.dataset.showAuthor !== 'false',
			showStoryType: container.dataset.showStoryType === 'true',
			allowCustomAmount: container.dataset.allowCustomAmount === 'true'
		};
		
		const root = createRoot(container);
		root.render(<GalleryEditor transformationSettings={transformationSettings} displaySettings={displaySettings} />);
	}
});